import {
  ValidationErrorResponse,
  type ValidationIssue,
} from "@bw-dwh/api/contracts/errors";
import { getActiveTenantSlug, getToken } from "@/lib/auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, "UNAUTHORIZED", "Missing or invalid session");
  }
}

export class ApiValidationError extends ApiError {
  constructor(
    public issues: ValidationIssue[],
    status = 422,
  ) {
    super(
      status,
      "VALIDATION_ERROR",
      issues.map((issue) => issue.message).join("; ") || "Validation failed",
    );
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Fetch wrapper that attaches a Clerk session JWT on every request and
 * normalizes error envelopes.
 *
 * Background-poll invariant (preserves commit 351a50d behavior): a 401
 * here is surfaced as UnauthorizedError but does NOT force sign-out. Clerk
 * auto-refreshes tokens every ~50s; transient 401s recover on the next poll.
 * Only an explicit `signOut()` call (Sidebar, Settings) ends the session.
 */
export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const token = await getToken();
  const tenantSlug = getActiveTenantSlug();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantSlug) headers["X-BW-Tenant"] = tenantSlug;

  const res = await fetch(path, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    cache: "no-store",
  });

  if (res.status === 401) {
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    let errBody: unknown = {};
    try {
      errBody = await res.json();
    } catch {
      /* ignore */
    }

    if (res.status === 422 || res.status === 400) {
      const validation = ValidationErrorResponse.safeParse(errBody);
      if (validation.success) {
        throw new ApiValidationError(validation.data.issues, res.status);
      }

      const legacy = errBody as { error?: { code?: string; message?: string } };
      if (legacy.error?.code === "VALIDATION_ERROR") {
        throw new ApiValidationError(
          [
            {
              path: [],
              code: legacy.error.code,
              message: legacy.error.message ?? "Validation failed",
            },
          ],
          res.status,
        );
      }
    }

    const legacy = errBody as { error?: { code?: string; message?: string } };
    throw new ApiError(
      res.status,
      legacy.error?.code ?? "UNKNOWN",
      legacy.error?.message ?? `${res.status} ${res.statusText}`,
    );
  }

  return (await res.json()) as T;
}
